/**
 * A refused save → the i18n key that explains it, so the trainer is told WHAT to do instead of
 * reading a generic "nie udało się zapisać".
 *
 * ── Match on the machine code, never on the message ──────────────────────────────────────────
 * The backend's rejections carry a stable `code` in the RFC 9457 `extensions` block, and that is the
 * only thing this module reads. The `title` and `detail` beside it are human-readable text the
 * server may reword, localise or shorten at any time; a client switching on those is a client whose
 * error handling breaks on a typo fix. `ApiError.code` is the accessor.
 *
 * ── Ported from `move-with-me-frontend/.../plans/builder/model/saveErrorCopy.ts` ──────────────
 * The map is mobile's, code for code, and so is the reasoning behind its two odd entries. Three
 * things changed on the way over, all of them at the interface rather than in the table:
 *
 * 1. **The lookup ALWAYS returns a key.** Mobile returns `null` for an unknown or absent code and
 *    leaves the caller to fall back to its own generic copy — which means every call site owns a
 *    piece of the error contract, and a new backend code degrades to whatever that site happened to
 *    do. Here the fallback is IN the table (`saveErrorUnknown`), so "a 400 with no code" is a case
 *    this module has answered rather than one the caller has to remember. Many 400s genuinely carry
 *    no code — a terminal assignment's read-only refusal and the sequence-consent refusal both do
 *    (ADR §8) — so this is the common path, not the edge.
 * 2. **`concurrency_conflict` is in the table.** Mobile leaves it out because its 409 always reaches
 *    the reload prompt. Here it is a real, if rare, terminal path: the 409 handler refetches the
 *    winning tree, and if THAT read fails there is nothing to diff and nothing to offer, so the
 *    trainer gets words instead of a dialog with two dead buttons.
 * 3. **The keys are `web:`-prefixed** and live in this app's own namespace (execution ADR §7).
 *
 * ── Two codes are OUR bug, not a rule the trainer hit ────────────────────────────────────────
 * `duplicate_slot_token` and `plan_phase_orders_not_contiguous` share one string, because they say
 * the same thing to the same person and a second identical entry would only drift.
 *
 * Neither is RECOVERED from, and that is deliberate. `draftToRequest` emits `order: index + 1`, so
 * the second is unreachable from this client at all — the backend added it for API-direct callers.
 * The first is reachable in principle, and the backend's own note asks the client to "re-mint the
 * duplicate token and resubmit" — but a duplicate is two slots claiming one identity and NOTHING in
 * the rejection says which of them is the impostor: the code carries no payload, and matching on the
 * message text is the thing this module exists to avoid. Re-minting the wrong one converts a stored
 * slot into a new one and resets the client's activation cycle and progression origin, which is
 * precisely the damage slot tokens were introduced to prevent. So the copy points at the one repair
 * that is always safe — reopen the plan, which re-seeds from the server, whose stored tokens are
 * unique by construction because the backend refused every duplicate. It says the fault is ours,
 * because it is.
 *
 * ── Two window refusals a DRAG can earn, so they are answered in words ───────────────────────
 * A moved exercise keeps the cycle window its trainer authored — `transplantedExercise` carries
 * `retirementCycle` and `excludedCycles` across, because dropping them would widen the row back over
 * every week the trainer cut. Nothing intersects that carried window with the TARGET day's own, and
 * silently intersecting it would be worse: it would throw away the trainer's cut without saying so.
 * So a drop whose two windows do not overlap reaches the backend and is refused with
 * `plan_window_empty`, and a drop that changes which cycles a row runs in at or below the consumed
 * prefix is refused with `plan_window_locked`. Both used to fall through to the generic "check your
 * connection", which is advice for a different problem entirely.
 *
 * Both are mode-generic, and their copy earns that by naming NO unit — "zakres", not "tygodnie" or
 * "cykle". The per-mode split below exists for messages that must count something; a message that
 * says *where* rather than *how many* stays one string, which is one fewer pair to drift.
 *
 * ── And one a per-cycle WINDOW edit can earn, which nothing else could ───────────────────────
 * `plan_override_on_sat_out_cycle` is `TemplateSetMaterializer`'s: a per-cell pin naming a cycle its
 * exercise no longer runs in. Nothing in this app could reach it while a day ran in every cycle of
 * its phase — the pins and the cycles moved together — and the calendar's "remove this training from
 * this week" is exactly the edit that separates them, on a week the trainer had pinned in the
 * builder's matrix. The copy says which repair is available, because the app cannot make it: the
 * refusal names no coordinate, so there is no pin this side could offer to drop.
 *
 * `plan_cycle_uninhabited` is deliberately NOT here. It was `EnsureEveryCycleIsInhabited`'s, which
 * ran on sequence plans alone and went with the sequence engine — so a row for it would be copy
 * nothing can display, aging quietly against a contract nobody re-reads.
 *
 * ── One refusal names the UNIT, so it has one entry per canvas ───────────────────────────────
 * A plan drawn on the rhythm tape counts cycles, not weeks. The mode is passed in rather than baked
 * into the key, so the contract stays a lookup rather than string surgery on a key name.
 *
 * The map is data and the lookup is pure, so the copy contract is testable with no renderer and no
 * i18next: `everyKnownRefusalCodeResolvesToAKeyThatExists` reads the real locale file.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. It returns KEYS; the caller
 * translates.
 */

import type { PlanSchedulingMode } from './planDraft.types';

/** The key a rejection resolves to, per scheduling mode. Identical on both when the copy names no unit. */
export interface SaveErrorCopyEntry {
  readonly date: string;
  readonly selfpaced: string;
}

/** Both modes resolve to one key — the copy names no unit. */
function bothModes(key: string): SaveErrorCopyEntry {
  return { date: key, selfpaced: key };
}

/** The key a save failure resolves to when the response carried no code this build knows. */
export const UNKNOWN_SAVE_ERROR_KEY = 'web:planBuilder.saveError.unknown';

/** Backend rejection code → the key that explains it, and what the trainer can do instead. */
export const SAVE_ERROR_CODE_KEYS: Readonly<Record<string, SaveErrorCopyEntry>> = {
  plan_phase_shrink_blocked: {
    date: 'web:planBuilder.saveError.phaseShrink',
    selfpaced: 'web:planBuilder.saveError.phaseShrinkCycles',
  },
  plan_phase_completed_locked: bothModes('web:planBuilder.saveError.phaseCompleted'),
  plan_phase_structure_locked: bothModes('web:planBuilder.saveError.structureLocked'),
  plan_mode_locked: bothModes('web:planBuilder.saveError.modeLocked'),
  plan_total_cycles_capped: {
    date: 'web:planBuilder.saveError.totalCyclesCapped',
    selfpaced: 'web:planBuilder.saveError.totalCyclesCappedCycles',
  },
  plan_window_empty: bothModes('web:planBuilder.saveError.windowEmpty'),
  plan_window_locked: bothModes('web:planBuilder.saveError.windowLocked'),
  plan_override_on_sat_out_cycle: bothModes('web:planBuilder.saveError.overrideOnSatOutCycle'),
  duplicate_slot_token: bothModes('web:planBuilder.saveError.appFault'),
  plan_phase_orders_not_contiguous: bothModes('web:planBuilder.saveError.appFault'),
  concurrency_conflict: bothModes('web:planBuilder.saveError.concurrency'),
  // An exercise the trainer cannot use, or a Buddy draft's slot still at exercise id 0 (ai-plan-builder R8).
  plan_exercise_unavailable: bothModes('web:planBuilder.saveError.exerciseUnavailable'),
};

/**
 * The i18n key for a refused save. **Always a key** — an unknown or absent code resolves to the
 * generic one rather than to `null`, so no caller has to carry half of this contract.
 *
 * `Object.hasOwn` before indexing: a bare lookup walks the prototype chain, so a backend code of
 * `"constructor"` would return a function rather than an entry.
 */
export function saveErrorCopyKey(
  code: string | null | undefined,
  mode: PlanSchedulingMode = 'date',
): string {
  if (!code || !Object.hasOwn(SAVE_ERROR_CODE_KEYS, code)) return UNKNOWN_SAVE_ERROR_KEY;
  return SAVE_ERROR_CODE_KEYS[code]?.[mode] ?? UNKNOWN_SAVE_ERROR_KEY;
}
